import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

function toDateKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AdminPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Auth + admin check
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/auth", { replace: true });
        return;
      }
      setUserEmail(sessionData.session.user.email ?? null);
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", sessionData.session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (cancelled) return;
      setIsAdmin(!!roles);
      setChecking(false);
    };
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate("/auth", { replace: true });
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  // Load unavailable + realtime
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.from("unavailable_dates").select("date");
      if (cancelled || !data) return;
      setUnavailable(new Set(data.map((r: { date: string }) => r.date)));
    };
    load();
    const channel = supabase
      .channel("unavailable_dates_admin")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "unavailable_dates" },
        () => load(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const markUnavailable = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setMessage(null);
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const rows = Array.from(selected)
      .filter((d) => !unavailable.has(d))
      .map((d) => ({ date: d, created_by: userId ?? null }));
    if (rows.length === 0) {
      setMessage("Selected dates are already unavailable.");
      setBusy(false);
      return;
    }
    const { error } = await supabase.from("unavailable_dates").insert(rows);
    if (error) setMessage(error.message);
    else {
      setMessage(`Marked ${rows.length} date(s) as unavailable.`);
      setSelected(new Set());
    }
    setBusy(false);
  };

  const markAvailable = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setMessage(null);
    const toDelete = Array.from(selected).filter((d) => unavailable.has(d));
    if (toDelete.length === 0) {
      setMessage("Selected dates are already available.");
      setBusy(false);
      return;
    }
    const { error } = await supabase.from("unavailable_dates").delete().in("date", toDelete);
    if (error) setMessage(error.message);
    else {
      setMessage(`Marked ${toDelete.length} date(s) as available.`);
      setSelected(new Set());
    }
    setBusy(false);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F0EBE0" }}>
        <p className="font-sans text-sm" style={{ color: "#7E7265" }}>Loading…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F0EBE0" }}>
        <div className="max-w-md text-center">
          <h1 className="font-serif font-light text-2xl mb-4" style={{ color: "#3C3530" }}>
            Access Restricted
          </h1>
          <p className="font-sans font-light text-sm mb-6" style={{ color: "#8C8478" }}>
            This account ({userEmail}) is signed in but is not the owner. Please contact the site administrator
            to be granted access.
          </p>
          <button
            onClick={signOut}
            className="px-8 py-3 text-sm tracking-[0.1em] uppercase font-sans font-medium rounded-full cursor-pointer"
            style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const todayKey = toDateKey(today);

  return (
    <div className="min-h-screen px-6 py-12" style={{ backgroundColor: "#F0EBE0", color: "#3C3530" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs tracking-[0.1em] uppercase font-sans font-medium" style={{ color: "#7E7265" }}>
              Owner Dashboard
            </p>
            <h1 className="font-serif font-light text-3xl mt-1" style={{ color: "#3C3530" }}>
              Manage Availability
            </h1>
          </div>
          <button
            onClick={signOut}
            className="text-xs tracking-[0.1em] uppercase font-sans cursor-pointer underline"
            style={{ color: "#7E7265" }}
          >
            Sign Out
          </button>
        </div>

        <p className="font-sans font-light text-sm mb-8" style={{ color: "#8C8478" }}>
          Click dates to select them, then choose Mark Unavailable or Mark Available. Changes appear instantly on
          the public calendar.
        </p>

        <div
          className="rounded-lg p-6 md:p-10"
          style={{ backgroundColor: "#FDFAF4", border: "1px solid #E4DDD4" }}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
              style={{ color: "#4E4228" }}
              aria-label="Previous month"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <h3 className="font-serif font-light text-xl md:text-2xl" style={{ color: "#3C3530" }}>
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </h3>
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
              style={{ color: "#4E4228" }}
              aria-label="Next month"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-sans font-medium uppercase tracking-[0.1em] py-2"
                style={{ color: "#8C8478" }}
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square" />;
              const key = toDateKey(d);
              const isUnavailable = unavailable.has(key);
              const isSelected = selected.has(key);
              const isToday = key === todayKey;

              let bg = "#F8F3EA";
              if (isUnavailable) bg = "#C8B898";
              if (isSelected) bg = "#4E4228";

              return (
                <button
                  key={i}
                  onClick={() => toggleSelect(key)}
                  className="aspect-square flex items-center justify-center text-sm font-sans rounded cursor-pointer transition-colors"
                  style={{
                    backgroundColor: bg,
                    color: isSelected ? "#F5F0E8" : "#3C3530",
                    border: isToday ? "1.5px solid #4E4228" : "1px solid transparent",
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs font-sans" style={{ color: "#7E7265" }}>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: "#F8F3EA", border: "1px solid #E4DDD4" }}></span>
              Available
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: "#C8B898" }}></span>
              Unavailable
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: "#4E4228" }}></span>
              Selected
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <button
            onClick={markUnavailable}
            disabled={busy || selected.size === 0}
            className="px-8 py-3 text-sm tracking-[0.1em] uppercase font-sans font-medium rounded-full cursor-pointer disabled:opacity-40"
            style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}
          >
            Mark Unavailable ({selected.size})
          </button>
          <button
            onClick={markAvailable}
            disabled={busy || selected.size === 0}
            className="px-8 py-3 text-sm tracking-[0.1em] uppercase font-sans font-medium rounded-full cursor-pointer disabled:opacity-40 border"
            style={{ borderColor: "#4E4228", color: "#4E4228", backgroundColor: "transparent" }}
          >
            Mark Available ({selected.size})
          </button>
          <button
            onClick={() => setSelected(new Set())}
            disabled={selected.size === 0}
            className="px-8 py-3 text-sm tracking-[0.1em] uppercase font-sans rounded-full cursor-pointer disabled:opacity-40"
            style={{ color: "#7E7265" }}
          >
            Clear
          </button>
        </div>

        {message && (
          <p className="text-sm text-center font-sans font-light mt-6" style={{ color: "#5E6A54" }}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
